/**
 * Central route SEO data — shared by routeSeoMap.js and scripts/generate-sitemap.js (CommonJS).
 * Each route must have a unique description for Google sitelinks and search snippets.
 */

const routeSeoMap = {
  '/': {
    title: 'Premier Software Development Company | Genzix',
    description:
      'Genzix is a premier software development company specializing in web apps, mobile applications, custom AI automation, and cloud infrastructure solutions.',
    keywords:
      'software development company, software development, web design, mobile apps, AI automation, cloud infrastructure, digital transformation, UI/UX design, digital marketing, Genzix',
    priority: '1.0',
    changefreq: 'weekly',
    navName: 'Web Design, Mobile Apps & AI Solutions',
    navDescription:
      'Custom web apps, mobile apps, AI automation and cloud solutions — built by Genzix for modern businesses worldwide.',
    ogImage: 'https://genzix.in/logo192.png',
    imageTitle: 'Genzix - Software Development Company',
    imageCaption:
      'Leading software development company specializing in web development, AI automation, and digital transformation',
  },

  '/services': {
    title: 'Custom Software & Web Development Services | Genzix',
    description:
      'Explore our full suite of software development services. We engineer custom mobile apps, cross-platform systems, enterprise CRMs, and robust web solutions.',
    keywords:
      'custom software development services, software development services, web app development, mobile app development India, AI automation services, UI/UX design, digital marketing, Genzix',
    priority: '0.9',
    changefreq: 'monthly',
    schemaType: 'Service',
  },

  '/projects': {
    title: 'Our Software Engineering & App Portfolio | Genzix',
    description:
      'Discover the real digital products we built. Browse our software portfolio featuring active food delivery apps, custom enterprise CRMs, and AI platforms.',
    keywords:
      'software engineering portfolio, Genzix portfolio, web development projects, app development portfolio, AI projects, food tech, community app, school management, Genzix work',
    priority: '0.9',
    changefreq: 'weekly',
    navName: 'Projects — Portfolio',
    navDescription:
      "Explore Genzix's completed projects: food-tech platforms, AI tools, community apps, civic tech, student platforms and more.",
    schemaType: 'CollectionPage',
  },

  '/aboutus': {
    title: 'About Genzix | Driven by Software Engineering Excellence',
    description:
      'Founded by an expert tech team, Genzix engineers high-performance web systems and enterprise software platforms designed for scalable business growth.',
    keywords:
      'about genzix software engineering, about Genzix, software development company India, web development team, AI specialists, product engineering',
    priority: '0.9',
    changefreq: 'monthly',
    schemaType: 'AboutPage',
  },

  '/contactus': {
    title: 'Contact Genzix | Hire a Top Software Development Agency',
    description:
      'Ready to scale your digital presence? Contact Genzix today to consult with our engineers and launch your custom software or web application project.',
    keywords:
      'hire software development agency, contact Genzix, software development quote, web app consultation, mobile app development, AI project inquiry',
    priority: '0.8',
    changefreq: 'monthly',
    navName: 'Contact Genzix',
    navDescription:
      'Get in touch with Genzix to discuss your next web, mobile app, or AI project. Free consultation available.',
    schemaType: 'ContactPage',
  },

  '/projects/contactus': {
    title: 'Contact Genzix | Hire a Top Software Development Agency',
    description:
      'Ready to scale your digital presence? Contact Genzix today to consult with our engineers and launch your custom software or web application project.',
    keywords:
      'hire software development agency, contact Genzix, software development quote, web app consultation, mobile app development, AI project inquiry',
    priority: '0.5',
    changefreq: 'monthly',
    canonicalPath: '/contactus',
    schemaType: 'ContactPage',
  },

  '/blog': {
    title: 'Genzix Tech Blog | Software & AI Insights',
    description:
      'Stay ahead of digital trends. Read technical articles, tutorials, and insights on web development, custom AI software, and modern cloud ecosystems.',
    keywords:
      'genzix technology insights blog, software development blog, web development insights, AI automation, cloud infrastructure, React development, DevOps, digital transformation',
    priority: '0.8',
    changefreq: 'weekly',
    schemaType: 'Blog',
  },

  '/careers': {
    title: 'Careers at Genzix | Join Our Software Team',
    description:
      'Explore open roles at Genzix — a fast-growing software development company building web apps, mobile apps, and AI products. Apply today.',
    keywords:
      'Genzix careers, software developer jobs India, remote developer jobs, join Genzix, tech jobs, hiring software engineers',
    priority: '0.8',
    changefreq: 'weekly',
    navName: 'Careers — Join Genzix',
    navDescription:
      'Open software engineering, design and growth roles at Genzix — apply to build web, mobile and AI products with us.',
    schemaType: 'CollectionPage',
  },

  '/shadab': {
    title: 'Shadab App Case Study | Custom Logistics & Delivery | Genzix',
    description:
      'Discover how Genzix engineered a high-performance custom application for Shadab Restaurant, optimizing real-time tracking and delivery logistics.',
    keywords:
      'shadab restaurant custom application, Shadab Restaurant, food delivery app, quick commerce, pan india delivery, restaurant management, branch admin, React Native, food tech, Genzix portfolio',
    priority: '0.9',
    changefreq: 'monthly',
    lastmod: '2026-04-07',
    navName: 'Shadab Restaurant',
    navDescription:
      'Full-stack food delivery app and website for Shadab Restaurant — Pan India delivery, Quick Commerce, Quick Delivery and branch-level admin, built by Genzix.',
    ogImage: 'https://genzixbackend.s3.ap-south-1.amazonaws.com/shadab_cover.jpg',
    imageTitle: 'Shadab Restaurant — Food Delivery App & Website',
    imageCaption:
      'Full-stack food delivery platform with Pan India delivery, Quick Commerce, Quick Delivery, and branch-level admin — built by Genzix',
    schemaType: 'CreativeWork',
  },

  '/projects/shadab': {
    title: 'Shadab Restaurant — Portfolio Case Study | Genzix',
    description:
      "Case study: Genzix delivered Shadab Restaurant's food tech stack — mobile app, web platform, Pan India delivery and multi-branch admin.",
    keywords: 'Shadab Restaurant case study, food delivery portfolio, Genzix projects, quick commerce app',
    priority: '0.9',
    changefreq: 'monthly',
    lastmod: '2026-04-07',
    canonicalPath: '/shadab',
    schemaType: 'CreativeWork',
  },

  '/ai': {
    title: 'AI Property Recommender — Real Estate Platform | Genzix',
    description:
      'Genzix built an AI-powered real estate platform for the US market — ranking properties by investment potential, crime rate, transport, schools and climate.',
    keywords:
      'custom ai automation solutions, AI property recommender, real estate AI, Crew AI, SERP AI, property ranking USA, FastAPI React, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/ai': {
    title: 'AI Property Recommender — Portfolio Case Study | Genzix',
    description:
      'Portfolio highlight: US real estate AI platform by Genzix, ranking properties by investment potential, crime, transport, schools and climate.',
    keywords: 'AI real estate case study, Crew AI portfolio, property recommender Genzix',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/ai',
    schemaType: 'CreativeWork',
  },

  '/clokam': {
    title: 'Clokam — Community Mobile App | Genzix',
    description:
      'Clokam is a community mobile app built with Flutter by Genzix — join groups, discover interests, manage events and build social connections.',
    keywords:
      'clokam platform development case study, Clokam app, community mobile app, Flutter community platform, events and groups app, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    navName: 'Clokam — Community App',
    navDescription:
      'Flutter-powered community mobile app for joining groups, discovering interests, and managing events — built by Genzix.',
    schemaType: 'CreativeWork',
  },

  '/projects/clokam': {
    title: 'Clokam — Community App Case Study | Genzix',
    description:
      'Genzix portfolio: Clokam LLC community app — Flutter groups, interest discovery, event management and social networking for local communities.',
    keywords: 'Clokam case study, community app development, Flutter portfolio Genzix',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/clokam',
    schemaType: 'CreativeWork',
  },

  '/spoorthi-crm': {
    title: 'Spoorthi CRM — School Management System | Genzix',
    description:
      'Spoorthi CRM is a school management platform by Genzix — real-time analytics, student management, fee collection, attendance and employee management.',
    keywords:
      'spoorthi crm software engineering, Spoorthi CRM, school management system, fee collection software, attendance tracking, education ERP, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/spoorthi-crm': {
    title: 'Spoorthi CRM — School ERP Case Study | Genzix',
    description:
      'Case study: Spoorthi CRM by Genzix — dashboards, student records, fee collection, attendance and employee management for schools.',
    keywords: 'Spoorthi CRM case study, school ERP Genzix, education software portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/spoorthi-crm',
    schemaType: 'CreativeWork',
  },

  '/student-chakra': {
    title: 'Student Chakra — Networking & Career Platform | Genzix',
    description:
      'Student Chakra is a student networking platform by Genzix — real-time messaging, study groups, job tracking and career management.',
    keywords:
      'student chakra educational application, Student Chakra, student networking app, study groups, career platform, academic resources, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/student-chakra': {
    title: 'Student Chakra — Student Platform Case Study | Genzix',
    description:
      'Portfolio: Student Chakra student life platform — messaging, study groups, job tracking and career tools engineered by Genzix.',
    keywords: 'Student Chakra case study, student networking Genzix, education app portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/student-chakra',
    schemaType: 'CreativeWork',
  },

  '/sevak': {
    title: 'SEVAK — Citizen Issue Resolution Mobile App | Genzix',
    description:
      'SEVAK is a citizen-first mobile app by Genzix — submit complaints, route them to the right department, and track resolution without office visits.',
    keywords:
      'sevak phone web platform case study, SEVAK app, civic tech, citizen complaint app, Flutter government app, Bhoomia International, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/sevak': {
    title: 'SEVAK — Civic Tech Case Study | Genzix',
    description:
      'Genzix portfolio: SEVAK civic platform for Bhoomia International — citizens submit complaints with email routing and transparent resolution tracking.',
    keywords: 'SEVAK case study, civic app Genzix, citizen services portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/sevak',
    schemaType: 'CreativeWork',
  },

  '/hubio': {
    title: 'Hubio — Car Wash Booking Platform | Genzix',
    description:
      'Genzix built hubio.co.in — a car wash platform with User, Driver and Partner roles, slot booking, PhonePe payments and live driver tracking.',
    keywords:
      'hubio cloud platform development, Hubio car wash, slot booking app, PhonePe integration, multi-role platform, Django car wash software, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/hubio': {
    title: 'Hubio — Car Wash Platform Case Study | Genzix',
    description:
      'Case study: Hubio car wash ecosystem by Genzix — three-role architecture, custom booking engine, PhonePe checkout and live driver maps.',
    keywords: 'Hubio case study, car wash app Genzix, booking platform portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/hubio',
    schemaType: 'CreativeWork',
  },

  '/unifyx': {
    title: 'Unifyx — Responsive Portfolio Website | Genzix',
    description:
      "Genzix built Unifyx's modern responsive portfolio website — a sleek platform showcasing services, work and brand story.",
    keywords:
      'unifyx system integration platform, Unifyx website, portfolio website development, responsive web design, Genzix portfolio, Ireland web design',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/unifyx': {
    title: 'Unifyx — Web Portfolio Case Study | Genzix',
    description:
      'Portfolio: Unifyx corporate showcase site by Genzix — fully responsive, performance-tuned presentation of services and project work for a modern brand.',
    keywords: 'Unifyx case study, portfolio website Genzix, responsive design project',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/unifyx',
    schemaType: 'CreativeWork',
  },

  '/fitsage': {
    title: 'Fitsage — AI Fitness & Wellness App | Genzix',
    description:
      'Fitsage by Genzix is an AI-driven fitness app with personalized diet plans, exercise recommendations, AI food detection and calorie tracking.',
    keywords:
      'fitsage mobile application development, Fitsage app, AI fitness app, diet planning app, calorie tracking, wellness platform, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/fitsage': {
    title: 'Fitsage — Fitness App Case Study | Genzix',
    description:
      'Case study: Fitsage health platform — Genzix delivered AI-personalized nutrition, workout guidance and smart habit tracking.',
    keywords: 'Fitsage case study, AI fitness Genzix, health app portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/fitsage',
    schemaType: 'CreativeWork',
  },

  '/spd': {
    title: 'SPD — ID Card Generator for Schools & Offices | Genzix',
    description:
      'Genzix built SPD — a web and mobile ID card generator for schools and offices with customizable layouts and a secure admin panel.',
    keywords:
      'spd digital portal development, SPD ID card generator, school ID cards, employee ID software, Flutter Django ID app, Genzix portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    schemaType: 'CreativeWork',
  },

  '/projects/spd': {
    title: 'SPD — ID Card Platform Case Study | Genzix',
    description:
      'Portfolio: SPD institutional ID system by Genzix — dynamic branded card templates and bulk credential download for schools and offices.',
    keywords: 'SPD case study, ID card software Genzix, institutional ID portfolio',
    priority: '0.8',
    changefreq: 'monthly',
    canonicalPath: '/spd',
    schemaType: 'CreativeWork',
  },
};

const blogSlugs = [
  { slug: 'custom-app-development', lastmod: '2024-12-05' },
  { slug: 'web-development-solutions', lastmod: '2024-12-10' },
  { slug: 'cloud-ai-automation', lastmod: '2024-12-15' },
  { slug: 'agents-ai-crew-ai-lang-graph', lastmod: '2024-12-20' },
];

const sitelinkNavOrder = ['/', '/projects', '/shadab', '/clokam', '/contactus'];

module.exports = { routeSeoMap, blogSlugs, sitelinkNavOrder };
